/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import { useCTAControl } from "@/hooks/useCTAControl";
import { usePWAInstallStore } from "@/stores/pwaInstallStore";
import { trackEvent } from "@/utils/analytics";
import {
  faArrowUpRightFromSquare,
  faDownload,
  faXmark,
} from "@/utils/iconLibraryTreeShake";
import { safeStorage } from "@/utils/storage";

const DISMISS_KEY = "pwa-install-banner-dismissed";
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function wasDismissedRecently(): boolean {
  const dismissed = safeStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  return Date.now() - Number(dismissed) < DISMISS_DURATION_MS;
}

const PWAInstallBanner = () => {
  const showBanner = usePWAInstallStore(s => s.showBanner);
  const platform = usePWAInstallStore(s => s.platform);
  const dismissStore = usePWAInstallStore(s => s.dismiss);
  const triggerInstall = usePWAInstallStore(s => s.triggerInstall);
  const { isActive, variant, dismiss, trackClick } = useCTAControl("pwa-install", {
    experimentKey: "cta-pwa-install",
    isEligible: showBanner && !wasDismissedRecently(),
  });

  const handleDismiss = () => {
    safeStorage.setItem(DISMISS_KEY, String(Date.now()));
    dismissStore();
    dismiss();
  };

  const handleInstall = async () => {
    trackClick({ platform: platform.type });
    const outcome = await triggerInstall();
    if (outcome) {
      trackEvent("pwa_install_outcome", {
        platform: platform.type,
        outcome,
        variant,
      });
    }
  };

  return (
    <Transition
      as="div"
      show={isActive}
      className="fixed bottom-4 right-4 z-50"
      enter="duration-500 ease-in-out transition-transform"
      enterFrom="translate-y-36"
      enterTo="translate-y-0"
      leave="duration-500 ease-in-out transition-transform"
      leaveFrom="translate-y-0"
      leaveTo="translate-y-36"
    >
      <div className="w-80 bg-slate-900 rounded-md px-4 py-3 shadow-lg border border-slate-700">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-600">
              <FontAwesomeIcon icon={faDownload} className="text-white text-sm" />
            </div>
            <p className="text-base font-semibold text-white">Install Galaxie</p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-200 p-1 -m-1"
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} className="text-sm" />
          </button>
        </div>

        <div className="mt-2.5">
          {platform.canPrompt ? (
            <ChromiumContent onInstall={handleInstall} onDismiss={handleDismiss} />
          ) : platform.type === "ios" ? (
            <IOSContent onDismiss={handleDismiss} />
          ) : platform.type === "macos-safari" ? (
            <MacOSSafariContent onDismiss={handleDismiss} />
          ) : null}
        </div>
      </div>
    </Transition>
  );
};

const ChromiumContent = ({
  onInstall,
  onDismiss,
}: {
  onInstall: () => void;
  onDismiss: () => void;
}) => (
  <>
    <p className="text-sm text-slate-300">
      Get instant access from your home screen with offline support.
    </p>
    <div className="flex items-center justify-between mt-3 gap-x-2">
      <button
        type="button"
        onClick={onDismiss}
        className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
      >
        Not now
      </button>
      <button
        type="button"
        onClick={onInstall}
        className="flex-1 rounded bg-blue-600 hover:bg-blue-500 px-3 py-1.5 text-sm font-medium text-white"
      >
        Install
      </button>
    </div>
  </>
);

const IOSContent = ({ onDismiss }: { onDismiss: () => void }) => (
  <>
    <p className="text-sm text-slate-300">
      Tap{" "}
      <FontAwesomeIcon
        icon={faArrowUpRightFromSquare}
        className="text-blue-400 mx-0.5"
      />{" "}
      Share, then <span className="font-medium text-white">"Add to Home Screen"</span>
    </p>
    <div className="flex justify-end mt-3">
      <button
        type="button"
        onClick={onDismiss}
        className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
      >
        Got it
      </button>
    </div>
  </>
);

const MacOSSafariContent = ({ onDismiss }: { onDismiss: () => void }) => (
  <>
    <p className="text-sm text-slate-300">
      In the menu bar, click <span className="font-medium text-white">File</span> →{" "}
      <span className="font-medium text-white">"Add to Dock"</span>
    </p>
    <div className="flex justify-end mt-3">
      <button
        type="button"
        onClick={onDismiss}
        className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
      >
        Got it
      </button>
    </div>
  </>
);

export default PWAInstallBanner;
